import { type MetaFunction } from '@remix-run/react';
import { type TypedResponse } from '@remix-run/server-runtime';
import { type RootLoaderType } from '#app/root.tsx';
import mdxStyles from '#app/styles/mdx.css';
import { type MdxListItem, type MdxPage } from '#types/mdx.ts';
import { cache, cachified } from './cache.server.ts';
import { compileMdx } from './compile-mdx.server.ts';
import {
  getBlogFile,
  getContentDirList,
  getContentFile } from
'./mdx-file.server.ts';
import { getUrl, typedBoolean } from './misc.tsx';
import { getSocialMetas } from './seo.ts';
import { type Timings } from './timing.server.ts';

export const mdxStyleLinks = () => {
  return [{ rel: 'stylesheet', href: mdxStyles }];
};

export type CachifiedOptions = {
  forceFresh?: boolean | string;
  request?: Request;
  ttl?: number;
  timings?: Timings;
};

const DEVMODE = 1;
let defaultTTL = 1000 * 60 * 60 * 24 * 14;
let defaultStaleWhileRevalidate = 1000 * 60 * 60 * 24 * 365 * 100;

if (DEVMODE && process.env.NODE_ENV === 'development') {
  defaultStaleWhileRevalidate = 1000 * 60;
  defaultTTL = 1000;
}

export async function getMdxPage(
{
  contentDir,
  slug



}: {contentDir: string;slug: string;},
options: CachifiedOptions = {})
{
  const key = `mdx-page:${contentDir}:${slug}:compiled`;
  let { forceFresh, ttl = defaultTTL, request, timings } = options;
  if (DEVMODE && process.env.NODE_ENV === 'development') {
    forceFresh = true;
  }
  const page = await cachified({
    key,
    cache,
    request,
    timings,
    ttl,
    staleWhileRevalidate: defaultStaleWhileRevalidate,
    forceFresh,
    checkValue: checkCompiledValue,
    getFreshValue: async () => {
      const compiledPage = await compileMdx(
        await getBlogFile(contentDir, slug),
        {
          './components/index.tsx': await getContentFile(
            'content/blog/components/index.tsx'
          )
        }
      ).catch((err) => {
        console.error(`Failed to get a fresh value for mdx:`, {
          contentDir,
          slug
        });
        return Promise.reject(err);
      });
      return compiledPage;
    }
  });
  if (!page) {
    // if there's no page, let's remove it from the cache
    void cache.delete(key);
  }
  return page;
}

const checkCompiledValue = (value: unknown) =>
typeof value === 'object' && (
value === null || 'code' in value && 'frontmatter' in value);

/**
 * This is useful for when you don't want to send all the code for a page to the client.
 */
function mapFromMdxPageToMdxListItem(page: MdxPage): MdxListItem {
  const { code, ...mdxListItem } = page;
  return mdxListItem;
}

const getDirListKey = (contentDir: string) => `${contentDir}:dir-list`;

export async function getMdxDirList(
contentDir: string,
options?: CachifiedOptions)
{
  let { forceFresh, ttl = defaultTTL, request, timings } = options ?? {};
  if (DEVMODE && process.env.NODE_ENV === 'development') {
    forceFresh = true;
  }
  const key = getDirListKey(contentDir);
  return cachified({
    cache,
    request,
    timings,
    ttl,
    staleWhileRevalidate: defaultStaleWhileRevalidate,
    forceFresh,
    key,
    checkValue: (value: unknown) => Array.isArray(value),
    getFreshValue: async () => {
      const fullContentDirPath = `content/${contentDir}`;
      const dirList = (await getContentDirList(fullContentDirPath)).
      filter(({ name, type }) => type === 'file' && name.endsWith('.mdx')).
      map(({ name, path }) => ({
        name,
        slug: path.
        replace(/\\/g, '/').
        replace(`${fullContentDirPath}/`, '').
        replace(/\.mdx$/, '')
      }));
      return dirList;
    }
  });
}

async function compileMdxCached({
  contentDir,
  slug,
  options




}: {contentDir: string;slug: string;options: CachifiedOptions;}) {
  const key = `${contentDir}:${slug}:compiled`;
  const page = await cachified({
    cache,
    ttl: defaultTTL,
    staleWhileRevalidate: defaultStaleWhileRevalidate,
    ...options,
    key,
    checkValue: checkCompiledValue,
    getFreshValue: async () => {
      const compiledPage = await compileMdx(
        await getBlogFile(contentDir, slug),
        {
          './components/index.tsx': await getContentFile(
            'content/blog/components/index.tsx'
          )
        }
      );
      if (compiledPage) {
        return {
          ...compiledPage,
          slug
        };
      } else {
        return null;
      }
    }
  });
  // if there's no page, remove it from the cache
  if (!page) {
    void cache.delete(key);
  }
  return page;
}

async function getMdxPagesInDirectory(
contentDir: string,
options: CachifiedOptions)
{
  const dirList = await getMdxDirList(contentDir, options);

  const pages = await Promise.all(
    dirList.map((pageData) =>
    compileMdxCached({ contentDir, ...pageData, options })
    )
  );
  return pages.filter(typedBoolean);
}

export async function getBlogMdxListItems(options: CachifiedOptions) {
  let { request, forceFresh, ttl = defaultTTL, timings } = options;
  if (DEVMODE && process.env.NODE_ENV === 'development') {
    forceFresh = true;
  }
  const key = 'blog:mdx-list-items';
  return cachified({
    cache,
    request,
    timings,
    ttl,
    staleWhileRevalidate: defaultStaleWhileRevalidate,
    forceFresh,
    key,
    getFreshValue: async () => {
      let pages = await getMdxPagesInDirectory('blog', options).then((allPosts) =>
      allPosts.filter((p) => !p.frontmatter.draft && !p.frontmatter.unlisted)
      );

      return pages.map(mapFromMdxPageToMdxListItem);
    }
  });
}

type ExtraMeta = Array<{[key: string]: string;}>;
type MetaLoader = () => Promise<
  TypedResponse<{
    page: MdxPage;
  }>>;


export const mdxPageMeta: MetaFunction<
  MetaLoader,
  {root: RootLoaderType;}> =
({ data, matches }) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const requestInfo = matches.find((m) => m.id === 'root')?.data.requestInfo;
  if (data?.page) {
    const extraMetaInfo = data.page.frontmatter.meta ?? {};
    const extraMeta: ExtraMeta = Object.entries(extraMetaInfo).reduce(
      (acc: ExtraMeta, [key, val]) => [...acc, { [key]: String(val) }],
      []
    );

    let title = data.page.frontmatter.title;

    return [
    ...extraMeta,
    ...getSocialMetas({
      title,
      description: data.page.frontmatter.description,
      url: getUrl(requestInfo),
      image: data.page.frontmatter.bannerUrl
    })];

  } else {
    return [
    { title: 'Not found' },
    { description: "We couldn't find this page" }];

  }
};